/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@atlaskit/css';
import AskIcon from '@atlaskit/icon-lab/core/ask';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ClockIcon from '@atlaskit/icon/core/clock';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import ToolsIcon from '@atlaskit/icon/core/tools';
import IconTile from '@atlaskit/icon/icon-tile';
import { ConfluenceIcon, FocusIcon, JiraIcon, JiraServiceManagementIcon } from '@atlaskit/logo';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { GoogleCalendarIcon } from '@atlassian/rovo-platform-ui-components/google-calendar-icon';

const actionCardProducts = [
	'google-calendar',
	'google-doc',
	'slack',
	'github',
	'sharepoint',
	'confluence',
	'jira',
	'jsm',
	'resumption',
	'capacity-planning',
	'plan',
	'focus',
	'focus-ask',
	'ask-question',
	'schedule',
] as const;

export type ActionCardProduct = (typeof actionCardProducts)[number];

export const getProductIcon = (product?: ActionCardProduct): JSX.Element | null => {
	switch (product) {
		case 'google-calendar':
			return <GoogleCalendarIcon label="" />;
		case 'google-doc':
			return <GoogleDocIcon label="" />;
		case 'slack':
			return <SlackIcon label="" />;
		case 'github':
			return <GithubIcon label="" />;
		case 'sharepoint':
			return <MicrosoftSharepointIcon label="" />;
		case 'confluence':
			return <ConfluenceIcon appearance="brand" shouldUseNewLogoDesign size="xsmall" />;
		case 'jira':
			return <JiraIcon appearance="brand" shouldUseNewLogoDesign size="xsmall" />;
		case 'jsm':
			return <JiraServiceManagementIcon appearance="brand" shouldUseNewLogoDesign size="xsmall" />;
		case 'resumption':
			return <IconTile icon={ToolsIcon} label="" appearance="gray" size="medium" />;
		case 'capacity-planning':
			return (
				<Tile label="" size="medium" backgroundColor="transparent" hasBorder>
					<ClockIcon label="" size="small" color={token('color.icon.accent.blue')} />
				</Tile>
			);
		case 'plan':
			return <RoadmapIcon label="" size="medium" color={token('color.icon.accent.blue')} />;
		case 'focus':
			return <FocusIcon appearance="brand" size="xsmall" />;
		case 'focus-ask':
			return (
				<Tile label="" size="medium" backgroundColor="transparent" hasBorder>
					<AskIcon label="" size="small" color={token('color.icon.subtlest')} />
				</Tile>
			);
		case 'ask-question':
			return <IconTile icon={QuestionCircleIcon} label="" appearance="gray" size="medium" />;
		case 'schedule':
			return <CalendarIcon label="" spacing="none" />;
		default:
			return null;
	}
};

type GoogleDocIconProps = {
	label?: string;
};

export const GoogleDocIcon = (props: GoogleDocIconProps): JSX.Element => (
	<svg
		width="28"
		height="28"
		viewBox="0 0 28 28"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-label={props.label}
	>
		<path
			d="M20.5938 24H8.40625C7.63281 24 7 23.3864 7 22.6364V5.36364C7 4.61364 7.63281 4 8.40625 4H16.8438L22 9V22.6364C22 23.3864 21.3672 24 20.5938 24Z"
			fill="#3086F6"
		/>
		<path d="M16.8438 4L22 9H16.8438V4Z" fill="#0C67D6" />
		<path
			d="M18.7188 12.8634H10.2812V11.7271H18.7188V12.8634ZM18.7188 14.4543H10.2812V15.5907H18.7188V14.4543ZM16.375 17.1816H10.2812V18.318H16.375V17.1816Z"
			fill="#FDFFFF"
		/>
	</svg>
);

type SlackIconProps = {
	label?: string;
};

export const SlackIcon = (props: SlackIconProps): JSX.Element => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-label={props.label}
	>
		<g clipPath="url(#clip0_271_181639)">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M9.33321 2C8.22767 2.00082 7.33294 2.89621 7.33375 3.99954C7.33294 5.10288 8.22849 5.99827 9.33403 5.99909H11.3343V4.00036C11.3351 2.89702 10.4396 2.00163 9.33321 2C9.33403 2 9.33403 2 9.33321 2V2ZM9.33321 7.33321H4.00077C2.89522 7.33402 1.99967 8.22941 2.00049 9.33275C1.99886 10.4361 2.89441 11.3315 3.99995 11.3331H9.33321C10.4388 11.3323 11.3343 10.4369 11.3335 9.33357C11.3343 8.22941 10.4388 7.33402 9.33321 7.33321V7.33321Z"
				fill="#36C5F0"
			></path>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M21.9998 9.33275C22.0006 8.22941 21.1051 7.33402 19.9995 7.33321C18.894 7.33402 17.9984 8.22941 17.9992 9.33275V11.3331H19.9995C21.1051 11.3323 22.0006 10.4369 21.9998 9.33275ZM16.6665 9.33275V3.99954C16.6673 2.89702 15.7726 2.00163 14.667 2C13.5615 2.00082 12.6659 2.89621 12.6668 3.99954V9.33275C12.6651 10.4361 13.5607 11.3315 14.6662 11.3331C15.7718 11.3323 16.6673 10.4369 16.6665 9.33275Z"
				fill="#2EB67D"
			></path>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M14.6665 21.9995C15.7721 21.9987 16.6677 21.1033 16.6668 19.9999C16.6677 18.8966 15.7721 18.0012 14.6665 18.0004H12.6663V19.9999C12.6654 21.1025 13.561 21.9979 14.6665 21.9995ZM14.6665 16.6654H19.9998C21.1054 16.6646 22.0009 15.7692 22.0001 14.6659C22.0018 13.5626 21.1062 12.6672 20.0006 12.6655H14.6674C13.5618 12.6663 12.6663 13.5617 12.6671 14.6651C12.6663 15.7692 13.561 16.6646 14.6665 16.6654V16.6654Z"
				fill="#ECB22E"
			></path>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M2.00073 14.6663C1.99992 15.7696 2.89547 16.665 4.00102 16.6659C5.10657 16.665 6.00212 15.7696 6.0013 14.6663V12.6667H4.00102C2.89547 12.6676 1.99992 13.563 2.00073 14.6663ZM7.33401 14.6663V19.9995C7.33237 21.1029 8.22792 21.9983 9.33347 21.9999C10.439 21.9991 11.3346 21.1037 11.3338 20.0003V14.6679C11.3354 13.5646 10.4398 12.6692 9.33429 12.6676C8.22792 12.6676 7.33319 13.563 7.33401 14.6663C7.33401 14.6671 7.33401 14.6663 7.33401 14.6663Z"
				fill="#E01E5A"
			></path>
		</g>
		<defs>
			<clipPath id="clip0_271_181639">
				<rect width="20" height="20" fill="white" transform="translate(2 2)"></rect>
			</clipPath>
		</defs>
	</svg>
);

type GithubIconProps = {
	label?: string;
};

export const GithubIcon = (props: GithubIconProps): JSX.Element => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-label={props.label}
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M8 2.14801C4.685 2.14801 2 4.83301 2 8.14801C2 10.803 3.7175 13.0455 6.1025 13.8405C6.4025 13.893 6.515 13.713 6.515 13.5555C6.515 13.413 6.5075 12.9405 6.5075 12.438C5 12.7155 4.61 12.0705 4.49 11.733C4.4225 11.5605 4.13 11.028 3.875 10.8855C3.665 10.773 3.365 10.4955 3.8675 10.488C4.34 10.4805 4.6775 10.923 4.79 11.103C5.33 12.0105 6.1925 11.7555 6.5375 11.598C6.59 11.208 6.7475 10.9455 6.92 10.7955C5.585 10.6455 4.19 10.128 4.19 7.83301C4.19 7.18051 4.4225 6.64051 4.805 6.22051C4.745 6.07051 4.535 5.45551 4.865 4.63051C4.865 4.63051 5.3675 4.47301 6.515 5.24551C6.995 5.11051 7.505 5.04301 8.015 5.04301C8.525 5.04301 9.035 5.11051 9.515 5.24551C10.6625 4.46551 11.165 4.63051 11.165 4.63051C11.495 5.45551 11.285 6.07051 11.225 6.22051C11.6075 6.64051 11.84 7.17301 11.84 7.83301C11.84 10.1355 10.4375 10.6455 9.1025 10.7955C9.32 10.983 9.5075 11.343 9.5075 11.9055C9.5075 12.708 9.5 13.353 9.5 13.5555C9.5 13.713 9.6125 13.9005 9.9125 13.8405C12.2825 13.0455 14 10.7955 14 8.14801C14 4.83301 11.315 2.14801 8 2.14801Z"
			fill="currentColor"
		/>
	</svg>
);

type MicrosoftSharepointIconProps = {
	label?: string;
};

export const MicrosoftSharepointIcon = (props: MicrosoftSharepointIconProps): JSX.Element => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-label={props.label}
	>
		<mask id="mask0_1701_19689" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
			<path d="M20 0H0V20H20V0Z" fill="white" />
		</mask>
		<g mask="url(#mask0_1701_19689)">
			<path
				d="M10.2328 11.4286C13.3153 11.4286 15.8142 8.87025 15.8142 5.71429C15.8142 2.55838 13.3153 0 10.2328 0C7.15024 0 4.65137 2.55838 4.65137 5.71429C4.65137 8.87025 7.15024 11.4286 10.2328 11.4286Z"
				fill="#036C70"
			/>
			<path
				d="M14.8838 16.1906C17.7095 16.1906 20.0001 13.8453 20.0001 10.9524C20.0001 8.05957 17.7095 5.71436 14.8838 5.71436C12.0582 5.71436 9.76758 8.05957 9.76758 10.9524C9.76758 13.8453 12.0582 16.1906 14.8838 16.1906Z"
				fill="#1A9BA1"
			/>
			<path
				d="M10.93 20C13.1135 20 14.8835 18.1879 14.8835 15.9524C14.8835 13.717 13.1135 11.9048 10.93 11.9048C8.74654 11.9048 6.97656 13.717 6.97656 15.9524C6.97656 18.1879 8.74654 20 10.93 20Z"
				fill="#37C6D0"
			/>
			<path
				opacity="0.1"
				d="M11.1628 5.15708V15.3189C11.1605 15.6721 10.9515 15.9893 10.6327 16.1237C10.531 16.1677 10.4219 16.1904 10.3117 16.1904H6.98145C6.9768 16.1094 6.9768 16.0333 6.9768 15.9523C6.97525 15.8728 6.97835 15.7933 6.9861 15.7142C7.07124 14.1921 7.98717 12.8491 9.35355 12.2428V11.3571C6.31257 10.8637 4.23802 7.93981 4.71991 4.82647C4.72326 4.80491 4.72671 4.78336 4.7303 4.76183C4.75345 4.60124 4.78607 4.44222 4.82797 4.28564H10.3117C10.781 4.28748 11.161 4.67657 11.1628 5.15708Z"
				fill="black"
			/>
			<path
				opacity="0.2"
				d="M9.84662 4.76196H4.73038C4.21355 7.86969 6.25531 10.8181 9.29075 11.3472C9.38275 11.3632 9.475 11.3768 9.56762 11.3882C8.12575 12.0882 7.07503 14.0763 6.98573 15.7143C6.97798 15.7934 6.97488 15.8729 6.97642 15.9524C6.97642 16.0334 6.97642 16.1096 6.98107 16.1906C6.98946 16.3506 7.00968 16.5098 7.04153 16.6667H9.84625C10.1911 16.6643 10.5009 16.4504 10.6322 16.1238C10.6751 16.0199 10.6974 15.9082 10.6974 15.7953V5.6334C10.6956 5.15306 10.3159 4.76406 9.84662 4.76196Z"
				fill="black"
			/>
			<path
				opacity="0.2"
				d="M9.84662 4.76196H4.73035C4.21361 7.87006 6.25568 10.8184 9.29149 11.3476C9.35362 11.3583 9.41587 11.3681 9.47825 11.3767C8.08287 12.1272 7.07361 14.1106 6.98616 15.7143H9.84662C10.3152 15.7107 10.6942 15.3227 10.6977 14.8429V5.6334C10.696 5.15289 10.316 4.7638 9.84662 4.76196Z"
				fill="black"
			/>
			<path
				opacity="0.2"
				d="M9.38134 4.76196H4.73019C4.24225 7.69606 6.03799 10.5216 8.85572 11.2534C7.78884 12.5014 7.13442 14.0628 6.986 15.7143H9.38134C9.85072 15.7126 10.2307 15.3234 10.2325 14.8429V5.6334C10.2322 5.15224 9.85134 4.76224 9.38134 4.76196Z"
				fill="black"
			/>
			<path
				d="M0.852563 4.76196H9.38C9.85087 4.76196 10.2325 5.15276 10.2325 5.63483V14.3653C10.2325 14.8473 9.85087 15.2382 9.38 15.2382H0.852563C0.3817 15.2382 0 14.8473 0 14.3653V5.63483C0 5.15276 0.3817 4.76196 0.852563 4.76196Z"
				fill="#04868B"
			/>
			<path
				d="M3.80825 9.89327C3.60846 9.75752 3.44157 9.57689 3.32035 9.36514C3.20289 9.14377 3.14433 8.89464 3.15057 8.64277C3.14011 8.30177 3.25251 7.96864 3.46639 7.70702C3.69112 7.44504 3.98162 7.25104 4.30638 7.14607C4.67651 7.02134 5.06418 6.95988 5.45382 6.96417C5.96621 6.94499 6.4779 7.01834 6.96545 7.18083V8.27602C6.75361 8.14464 6.52287 8.04827 6.28172 7.99039C6.02007 7.92464 5.7516 7.89177 5.48218 7.89227C5.1981 7.88164 4.91582 7.94277 4.66034 8.07039C4.46307 8.15752 4.33516 8.35614 4.33475 8.57602C4.33395 8.70952 4.38401 8.83802 4.47429 8.93414C4.58091 9.04764 4.70705 9.13989 4.84638 9.20652C5.00142 9.28564 5.23397 9.39064 5.54406 9.52177C5.5782 9.53289 5.61147 9.54652 5.64358 9.56277C5.94877 9.68489 6.24317 9.83352 6.5236 10.007C6.73595 10.1411 6.91417 10.3249 7.0436 10.5433C7.17628 10.7908 7.2406 11.0706 7.22963 11.3528C7.24478 11.7029 7.14016 12.0474 6.93382 12.327C6.72816 12.5841 6.45319 12.7735 6.14312 12.8718C5.77844 12.9888 5.39781 13.0454 5.01569 13.0394C4.67282 13.041 4.33047 13.0124 3.99242 12.9536C3.707 12.9059 3.42845 12.822 3.16314 12.7041V11.5494C3.41675 11.7349 3.70007 11.8734 4.00033 11.9589C4.29958 12.0544 4.61066 12.1054 4.92406 12.1104C5.21412 12.1291 5.50367 12.0663 5.76127 11.9285C5.94172 11.8243 6.05147 11.6268 6.04686 11.4146C6.04806 11.2671 5.99103 11.1253 5.88872 11.0213C5.76147 10.8934 5.61415 10.7883 5.45291 10.7104C5.26686 10.6151 4.99291 10.4895 4.63105 10.3336C4.34317 10.2153 4.06755 10.0676 3.80825 9.89327Z"
				fill="white"
			/>
		</g>
	</svg>
);
